// @flow
/* -----------------Globals--------------- */
import React, { useEffect, useContext } from 'react';

/* -----------------Third parties--------------- */
import API, { graphqlOperation } from '@aws-amplify/api';

/* -----------------Nextjs--------------- */
import { BreadcrumbJsonLd } from 'next-seo';

/* -----------------Context--------------- */
import { pageVariant, PageContext } from '@components/common/PageContext';
import type { NextContext } from 'common/types';

/* -----------------Utils--------------- */
import { baseUrl } from 'common/urlConstants';
import {
  getStemPreview,
  getDescription,
  getQuestionSlug,
} from '@components/questionset/utils';
import { encodeStringToSlug } from '@components/common/utils';

import {
  questionDetailsQuery,
  questionSetQuery,
  bundleNameQuery,
} from '@components/head/jsonLdUtils';

/* -----------------Child Components--------------- */
import QuestionUnauth from '@components/questionset/QuestionUnauth';

/* -----------------Types--------------- */
type Props = {
  question: any,
  jsonLdBreadcrumbs: *,
  questionSetNumericId: number
}

/* -----------------Page--------------- */
const Page = (props: Props) => {
  const {
    question,
    jsonLdBreadcrumbs,
    questionSetNumericId,
  } = props;

  const { setVariant } = useContext(PageContext);

  useEffect(() => {
    if (typeof setVariant === 'function') setVariant(pageVariant.static);
  }, [setVariant]);

  if (!question) {
    return null;
  }
  return (
    <>
      <BreadcrumbJsonLd itemListElements={jsonLdBreadcrumbs} />
      <QuestionUnauth
        questionSetNumericId={questionSetNumericId}
        breadcrumbs={jsonLdBreadcrumbs}
        {...question}
      />
    </>
  );
};

export const getStaticPaths = async () => {
  // Get the paths we want to pre-render based on posts
  const paths = [];
  return { paths, fallback: true };
};

export const getStaticProps = async (context: NextContext) => {
  // Get blog post id from URL
  const { id: qSetId, slug } = context.params;
  const questionSetNumericId = Number(qSetId);
  const numericId = Number(slug[0]);

  const res = await API.graphql(graphqlOperation(
    questionDetailsQuery, { numericId, questionSetNumericId },
  ));
  // Return 404 error if data not found
  if (!res.data) {
    return {
      notFound: true,
    };
  }
  const questionSetRes = await API.graphql(graphqlOperation(
    questionSetQuery, { numericId: questionSetNumericId },
  ));

  const cachedQuestionDetails = res.data.questionDetails;
  const cachedQuestionSetDetails = questionSetRes.data.questionSetDetails;
  const { question } = cachedQuestionDetails;
  // Get canonical URL for Question
  const {
    stem,
    choices: answers,
    // datePublished,
    // dateModified,
  } = question;
  const stemPreview = getStemPreview(stem);
  const description = getDescription(answers);
  const questionSetPath = `questions/${questionSetNumericId}`;
  const questionPath = `question/${numericId}/${getQuestionSlug(stem)}`;
  const url = `${baseUrl}/${questionSetPath}/${questionPath}`;

  // Get canonical URL for Question Set
  const {
    // id: questionSetId,
    questionSet: {
      name: questionSetTitle,
      owner: author,
      bundleId,
    },
  } = cachedQuestionSetDetails;

  const questionSetUrl = `${baseUrl}${questionSetPath}/${encodeStringToSlug(questionSetTitle)}`;

  // Get canonical URL for Bundle
  const getBundleJsonLD = async () => {
    if (bundleId) {
      const {
        data: {
          bundleDetails: {
            bundle: {
              name,
            },
          },
        },
      } = await API.graphql(graphqlOperation(bundleNameQuery, {
        numericId: bundleId,
      }));
      const bundlePath = `premium/${bundleId}/${encodeStringToSlug(name)}`;
      const bundleUrl = `${baseUrl}${bundlePath}`;
      const bundleJsonLd = {
        position: 1,
        name,
        item: bundleUrl,
      };
      return bundleJsonLd;
    }
    return null;
  };

  const bundleJsonLd = await getBundleJsonLD();
  // Get breadcrumbs for SEO
  const jsonLdBreadcrumbs = [
    ...(bundleJsonLd ? [bundleJsonLd] : []),
    {
      position: bundleJsonLd ? 2 : 1,
      name: questionSetTitle,
      item: questionSetUrl,
    },
    {
      position: bundleJsonLd ? 3 : 2,
      name: stemPreview,
      item: url,
    },
  ];
  return {
    props: {
      question,
      jsonLdBreadcrumbs,
      path: questionPath,
      questionSetNumericId,
      meta: {
        title: stemPreview,
        description,
        // datePublished,
        // dateModified,
        url,
        authorName: author,
      },
    },
    revalidate: 60,
  };
};

export default Page;
